/**
 * Enhanced GSAP Slide Preview Component
 * Full-screen GSAP slide viewer with external React-based controls
 * Uses postMessage communication with iframe for clean separation
 */

import { useEffect, useRef, useState, useCallback, useImperativeHandle, forwardRef } from 'react';
import { Button } from '../ui/button';
import {
  ChevronLeft, Info, Edit, Maximize2, Minimize2,
  RefreshCcw, Loader2, History, Share2, Globe
} from 'lucide-react';
import { GSAP_VIEWER_CONFIG, DEFAULT_SLIDE_RESOLUTION } from '../../config/gsapViewer';
import { UnifiedSlide } from '../../types/gsapSlide';
import { slidesAPI, filesystemAPI } from '../../services/api';
import { useVideoWorkspaceStore } from '../../stores/videoWorkspaceStore';
import { cn } from '../../lib/utils';
import { GsapPlayerControls, AutoHidePlayerControls } from './GsapPlayerControls';
import { GsapEditToolbar } from './GsapEditToolbar';
import { ElementProperties, AIEditQueueItem } from './PropertyPanel';
import { ElementListItem } from './ElementListPanel';
// NOTE: Save status indicator disabled (no auto-save)
// import { SaveStatusIndicator, SaveStatus } from './SaveStatusIndicator';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { PexelsSearchModal } from '../pexels/PexelsSearchModal';
import { VersionHistoryPanel, VersionInfo } from './VersionHistoryPanel';
import { VersionSwitcher } from './VersionSwitcher';
import { GroupingModal, GroupMetadata } from './GroupingModal';
import { PublishSlideModal } from './PublishSlideModal';
import { ConfirmationDialog } from '../ui/confirmation-dialog';
import { ScriptAudioPanel } from './ScriptAudioPanel';
import { ElementSuggestionMetadata } from '../../types/elementSuggestion';

interface GsapSlidePreviewEnhancedProps {
  slide: UnifiedSlide;
  conversationId?: string;
  outlineSlideId?: string;  // Outline position (e.g., "slide-007") for AI edit messages
  onBackToGallery?: () => void;
  isTemplate?: boolean;
  onApplyTemplate?: () => Promise<void>;
  className?: string;
  renderControls?: boolean;
  /** False where the page owns the F key (Studio B). */
  fullscreenKey?: boolean;
  onPlayerStateChange?: (state: PlayerState) => void;
  onEditToolbarStateChange?: (state: { selectedCount: number; canUndo: boolean; canRedo: boolean }) => void;
  /** Called when edit mode changes in the iframe */
  onEditModeChange?: (isEditMode: boolean) => void;
  /** Called when batched save completes successfully (for video workspace to delay invalidation) */
  onSaveComplete?: () => void;
  /** Slide resolution override (e.g. { width: 1080, height: 1080 } for 1:1). Defaults to 1920x1080. */
  resolution?: { width: number; height: number };
}

interface PlayerState {
  isPlaying: boolean;
  currentTime: number;
  duration: number;
  playbackRate: number;
  isLoading: boolean;
  slideTitle: string;
}

interface AssetInfo {
  url: string;
  type: 'image' | 'video' | 'icon';
  isLocal: boolean;
  uniqueId: string;         // Unique position-based ID
  layerIndex: number;       // Which layer contains this asset
  elementPath: string;      // CSS selector or element path (class-based)
  elementId?: string;       // data-element-id attribute (unique identifier)
  propertyType: string;     // 'img-src', 'bg-image', 'video-src', etc.
  context?: string;         // Original context for display
}

export interface GsapSlidePreviewRef {
  play: () => void;
  pause: () => void;
  togglePlay: () => void;
  seek: (time: number) => void;
  restart: () => void;
  step: (seconds: number) => void;
  setSpeed: (rate: number) => void;
  toggleEditMode: () => void;
  undo: () => void;
  redo: () => void;
  deleteSelection: () => void;
  deselectAll: () => void;
  groupSelection: () => void;
  finishEdit: () => void;
  cancelEdit: () => void;
  replaceAsset: () => void;
  selectTool: () => void;
}

export const GsapSlidePreviewEnhanced = forwardRef<GsapSlidePreviewRef, GsapSlidePreviewEnhancedProps>(({
  slide,
  conversationId,
  outlineSlideId,
  onBackToGallery,
  isTemplate = false,
  onApplyTemplate,
  className = '',
  renderControls = true,
  fullscreenKey = true,
  onPlayerStateChange,
  onEditToolbarStateChange,
  onEditModeChange,
  onSaveComplete,
  resolution = DEFAULT_SLIDE_RESOLUTION,
}, ref) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const doneDebounceRef = useRef<NodeJS.Timeout | null>(null);
  const [isApplyingTemplate, setIsApplyingTemplate] = useState(false);
  const [scale, setScale] = useState(0.5);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const isEditModeRef = useRef(false); // Ref to track edit mode without causing effect re-runs
  const onEditModeChangeRef = useRef(onEditModeChange); // Ref for callback to avoid stale closure
  onEditModeChangeRef.current = onEditModeChange; // Keep ref up to date
  const [isRecompiling, setIsRecompiling] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Edit toolbar state
  const [editToolbarState, setEditToolbarState] = useState({
    selectedCount: 0,
    canUndo: false,
    canRedo: false,
  });

  // Property panel state (setters used for iframe message handling, values unused after PropertyPanel removal)
  const [_selectedElementProps, setSelectedElementProps] = useState<ElementProperties[]>([]);

  // Element list panel state (setters used for iframe message handling, values unused after PropertyPanel removal)
  const [_elementList, setElementList] = useState<ElementListItem[]>([]);

  // AI Edit Queue state
  const [aiEditQueue, setAIEditQueue] = useState<AIEditQueueItem[]>([]);
  const [_isProcessingAI, setIsProcessingAI] = useState(false);

  // NOTE: Save status tracking disabled (no auto-save)
  // const [saveStatus, setSaveStatus] = useState<SaveStatus>('saved');

  // Asset-related state
  const [assets, setAssets] = useState<Map<string, AssetInfo>>(new Map());
  const [selectedAsset, setSelectedAsset] = useState<AssetInfo | null>(null);
  const [showPexelsModal, setShowPexelsModal] = useState(false);
  // @ts-ignore - Unused but setter is used for tracking purposes
  const [replacingAsset, setReplacingAsset] = useState<string | null>(null);
  const [assetHistory, setAssetHistory] = useState<any[]>([]); // All unique assets from slide history
  const [projectAssets, setProjectAssets] = useState<any[]>([]); // Project-wide assets

  // Edit mode asset replacement state - tracks the asset selected in edit mode for replacement
  const [selectedAssetForReplace, setSelectedAssetForReplace] = useState<{
    elementId: string;
    selector: string;
    assetType: 'image' | 'video' | 'background' | 'icon' | 'lottie';
    currentUrl: string;
    suggestionMetadata?: ElementSuggestionMetadata;
  } | null>(null);

  // Version-related state
  const [versions, setVersions] = useState<VersionInfo[]>([]);
  const [currentVersion, setCurrentVersion] = useState<number | null>(null);
  const [versioningEnabled, setVersioningEnabled] = useState(false);
  const [isSwitchingVersion, setIsSwitchingVersion] = useState(false);
  const [showVersionHistory, setShowVersionHistory] = useState(false);

  // Grouping modal state
  const [showGroupingModal, setShowGroupingModal] = useState(false);
  const [groupingElementCount, setGroupingElementCount] = useState(0);

  // Exit confirmation state
  const [showExitConfirmation, setShowExitConfirmation] = useState(false);

  // Save status state for visual feedback during save operations
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved'>('idle');

  // Publish/share state
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [publishStatus, setPublishStatus] = useState<{
    isPublished: boolean;
    shareLinks: Array<{
      id: string;
      shareToken: string;
      shareUrl: string;
      isActive: boolean;
      expiresAt: string | null;
      viewCount: number;
      lastViewedAt: string | null;
      createdAt: string;
      createdBy: string;
      revokedAt: string | null;
    }>;
  } | null>(null);
  const [isLoadingPublishStatus, setIsLoadingPublishStatus] = useState(false);

  // Player state synced from iframe
  const [playerState, setPlayerState] = useState<PlayerState>({
    isPlaying: false,
    currentTime: 0,
    duration: 0,
    playbackRate: 1,
    isLoading: true,
    slideTitle: slide.title || 'Loading...',
  });

  // Access pending seek time from store (used for version restore UX)
  // Access preview seek to end flag (used for filmstrip preview UX)
  const { pendingSeekTime, clearPendingSeekTime, previewSeekToEnd, setPreviewSeekToEnd } = useVideoWorkspaceStore();
  const pendingSeekTimeRef = useRef<number | null>(null);

  // Track pending seek time in ref to avoid stale closures
  useEffect(() => {
    pendingSeekTimeRef.current = pendingSeekTime;
  }, [pendingSeekTime]);

  // Extract slideId from slide data
  // Priority: slideIdentifier (from serve API) > path extraction > slide.id
  // In video mode, child conversations always store slides at 'slide-001' internally,
  // but the outline uses sequential IDs (slide-001, slide-002, etc.)
  // The serve API returns slideIdentifier with the correct GCS path.
  const extractSlideId = (): string => {
    // First: prefer slideIdentifier from serve API (most reliable for video mode)
    if ((slide as any).slideIdentifier) {
      return (slide as any).slideIdentifier;
    }

    // Second: try to extract from path
    if (slide.path) {
      const match = slide.path.match(/slides\/(slide-\d+)\//);
      if (match) {
        return match[1];
      }
    }

    // Fallback: use slide.id if available
    return slide.id || 'unknown';
  };

  const slideId = extractSlideId();

  // Build API URL for JSON file serving
  // For templates, use the slideJsonUrl from metadata
  // For GCS-backed conversations, use the new versioned endpoint
  // For legacy conversations, fallback to filesystem endpoint
  const jsonApiUrl = (slide.metadata as any)?.slideJsonUrl
    || slidesAPI.getJSONUrl(conversationId || '', slideId);

  // Debug logging for component props
  useEffect(() => {
    console.log('🔍 [GsapSlidePreviewEnhanced] Component mounted/updated:', {
      conversationId,
      slideId: slide.id,
      slidePath: slide.path,
      hasConversationId: !!conversationId,
      hasSlideId: !!slide.id
    });
  }, [conversationId, slide.id]);

  // Load versions on mount and when slide changes
  useEffect(() => {
    const loadVersions = async () => {
      // Skip version loading for templates
      if (slide.metadata?.isTemplate) {
        console.log('ℹ️ [Versioning] Skipping version load for template slide');
        setVersioningEnabled(false);
        setVersions([]);
        setCurrentVersion(null);
        return;
      }

      // Debug: Log why version loading might be skipped
      if (!conversationId) {
        console.warn('⚠️ [Versioning] Skipping version load: conversationId is missing/undefined');
        return;
      }
      if (!slideId) {
        console.warn('⚠️ [Versioning] Skipping version load: slideId is missing/undefined');
        return;
      }

      console.log('📋 [Versioning] Loading versions for slide:', slideId);

      try {
        const versionData = await slidesAPI.listVersions(conversationId, slideId);
        console.log('✅ [Versioning] Loaded versions:', {
          enabled: versionData.enabled,
          count: versionData.versions?.length || 0,
          currentVersion: versionData.currentVersion
        });
        setVersioningEnabled(versionData.enabled);
        setVersions(versionData.versions || []);
        setCurrentVersion(versionData.currentVersion);
      } catch (error) {
        console.error('❌ [Versioning] Failed to load versions:', error);
        // Silently fail - versioning is optional
        setVersioningEnabled(false);
        setVersions([]);
        setCurrentVersion(null);
      }
    };

    loadVersions();
  }, [conversationId, slideId, slide.metadata?.isTemplate]);

  // Refresh versions when slide is externally updated (new version created)
  useEffect(() => {
    if (!conversationId || !slideId || slide.metadata?.isTemplate) return;

    const handleSlidesUpdated = async (event: Event) => {
      const customEvent = event as CustomEvent;
      const { conversationId: updatedConvId } = customEvent.detail || {};

      // Only refresh if this conversation's slides were updated
      if (updatedConvId === conversationId) {
        console.log('🔄 [Versioning] Slide updated externally, refreshing versions');
        try {
          const versionData = await slidesAPI.listVersions(conversationId, slideId);
          console.log('✅ [Versioning] Refreshed versions after update:', {
            count: versionData.versions?.length || 0,
            currentVersion: versionData.currentVersion
          });
          setVersions(versionData.versions || []);
          setCurrentVersion(versionData.currentVersion);
        } catch (error) {
          console.error('❌ [Versioning] Failed to refresh versions after update:', error);
        }
      }
    };

    window.addEventListener('slides-updated', handleSlidesUpdated);
    return () => window.removeEventListener('slides-updated', handleSlidesUpdated);
  }, [conversationId, slideId, slide.metadata?.isTemplate]);

  // Load publish status on mount
  useEffect(() => {
    const loadPublishStatus = async () => {
      // Skip for templates
      if (slide.metadata?.isTemplate) {
        console.log('ℹ️ [Publish] Skipping publish status for template slide');
        return;
      }

      if (!conversationId || !slideId) {
        return;
      }

      setIsLoadingPublishStatus(true);

      try {
        const status = await slidesAPI.getPublishStatus(conversationId, slideId);
        console.log('✅ [Publish] Loaded publish status:', {
          isPublished: status.isPublished,
          activeLinks: status.shareLinks.filter(l => l.isActive).length
        });
        setPublishStatus(status);
      } catch (error) {
        console.error('❌ [Publish] Failed to load publish status:', error);
        // Silently fail - publishing is optional
        setPublishStatus(null);
      } finally {
        setIsLoadingPublishStatus(false);
      }
    };

    loadPublishStatus();
  }, [conversationId, slideId, slide.metadata?.isTemplate]);

  // Save slide changes
  // NOTE: Auto-save disabled - edits now go to chat for AI processing
  // Keeping this function commented out for future use if needed
  /*
  const handleSaveChanges = async (editData: any) => {
    try {
      setSaveStatus('saving');
      console.log('💾 Saving slide changes...', editData);

      // Build the save payload
      const savePayload = {
        conversationId: conversationId || '',
        slideId: slide.id,
        slidePath: slide.path,
        editData: editData,
        timestamp: new Date().toISOString()
      };

      console.log('📦 Save payload:', savePayload);

      // Call the actual save API endpoint
      const result = await slidesAPI.saveSlideEdits(savePayload);

      console.log('✅ Save successful:', result);
      setSaveStatus('saved');

    } catch (error) {
      console.error('❌ Save failed:', error);
      setSaveStatus('error');

      // Reset to unsaved after showing error
      setTimeout(() => {
        setSaveStatus('unsaved');
      }, 5000);
    }
  };
  */


  // Build GSAP viewer URL with external UI mode
  const viewerUrl = GSAP_VIEWER_CONFIG.buildUrl(jsonApiUrl, {
    editMode: true, // Enable edit mode support
    controls: false,
    uiMode: 'external', // Tell iframe to use external UI
    conversationId,
    resolution,
  });

  // Move console log to useEffect to avoid logging on every render
  useEffect(() => {
    console.log('🎬 GsapSlidePreviewEnhanced - Mounted:', {
      slideId: slide.id,
      viewerUrl,
      uiMode: 'external',
    });
  }, [slide.id, viewerUrl]);

  // Listen to iframe state broadcasts
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data.source === 'gsap-renderer') {
        // Handle edit toolbar state updates
        if (event.data.type === 'editToolbarState') {
          setEditToolbarState(event.data.state);
          onEditToolbarStateChange?.(event.data.state);

          // NOTE: Save status tracking disabled (no auto-save)
          // if (event.data.state.canUndo && saveStatus === 'saved') {
          //   setSaveStatus('unsaved');
          // }
        }

        // Handle element selection updates
        if (event.data.type === 'elementPropertiesUpdate') {
          console.log('📊 Element properties updated:', event.data.properties);
          setSelectedElementProps(event.data.properties || []);
        }

        // Handle selected asset info for Replace button in edit mode
        if (event.data.type === 'selectedAssetInfo') {
          const data = event.data.data;
          console.log('🖼️ Selected asset info:', data);
          if (data?.isReplaceableAsset && data?.elementId) {
            setSelectedAssetForReplace({
              elementId: data.elementId,
              selector: data.selector,
              assetType: data.assetType === 'background' ? 'image' : data.assetType, // Treat background as image for Pexels
              currentUrl: data.currentUrl,
              suggestionMetadata: data.suggestionMetadata || undefined
            });
          } else {
            setSelectedAssetForReplace(null);
          }
        }

        // Handle element list updates
        if (event.data.type === 'elementListUpdate') {
          console.log('📋 Element list updated:', event.data.elements);
          setElementList(event.data.elements || []);
        }

        // Handle AI edit queue updates
        if (event.data.type === 'aiEditAdded') {
          console.log('🤖 AI edit added to queue:', event.data.item);
          setAIEditQueue(prev => [...prev, event.data.item]);
        }

        if (event.data.type === 'aiQueueUpdated') {
          console.log('🤖 AI queue updated:', event.data.queue);
          setAIEditQueue(event.data.queue || []);
        }

        if (event.data.type === 'aiProcessingStarted') {
          console.log('🤖 AI processing started');
          setIsProcessingAI(true);
        }

        if (event.data.type === 'aiProcessingComplete') {
          console.log('🤖 AI processing complete');
          setIsProcessingAI(false);
          setAIEditQueue([]);
        }

        // Handle grouping metadata request from iframe
        if (event.data.type === 'requestGroupMetadata') {
          console.log('📦 Grouping metadata requested for', event.data.elementCount, 'elements');
          setGroupingElementCount(event.data.elementCount);
          setShowGroupingModal(true);
        }

        // Handle batched save from edit mode
        if (event.data.type === 'saveBatchedChanges') {
          console.log('💾 Saving batched changes:', event.data.editData);
          handleBatchedSave(event.data.editData);
        }

        // Handle edit completion - just log, no longer dispatch to chat
        // Direct edits are saved via saveBatchedChanges, AI edits use the queue system
        if (event.data.type === 'editsComplete') {
          console.log('📝 Edit completed (direct save mode):', event.data.editData);
          return;
        }

        // Handle regular state updates
        // Only update state if values actually changed to avoid unnecessary re-renders
        if (event.data.state) {
          setPlayerState(prev => {
            const newState = event.data.state;

            // Check if any value actually changed
            const hasChanges = Object.keys(newState).some(key =>
              prev[key as keyof typeof prev] !== newState[key]
            );

            // Only log significant changes (not time updates)
            if (hasChanges && !('currentTime' in newState && Object.keys(newState).length === 1)) {
              console.log('📨 GsapSlidePreviewEnhanced - State changed:', newState);
            }

            // Check for edit mode state - use ref to avoid dependency on isEditMode state
            if (newState?.editMode !== undefined && newState.editMode !== isEditModeRef.current) {
              console.log('📝 Edit mode changing:', isEditModeRef.current, '->', newState.editMode);
              isEditModeRef.current = newState.editMode; // Update ref first
              setIsEditMode(newState.editMode); // Then update state for UI

              // Notify parent via callback (used by VideoWorkspacePanels to sync store)
              onEditModeChangeRef.current?.(newState.editMode);

              // Notify parent to switch to preview-only mode when entering edit mode
              if (newState.editMode === true) {
                window.dispatchEvent(new CustomEvent('slideEditModeChanged', {
                  detail: { isEditMode: true }
                }));
              }

              // Clear saving state when exiting edit mode
              if (newState.editMode === false) {
                setIsSaving(false);
              }
            }

            if (hasChanges && onPlayerStateChange) {
              onPlayerStateChange(hasChanges ? { ...prev, ...newState } : prev);
            }

            return hasChanges ? { ...prev, ...newState } : prev;
          });
        }
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []); // No dependencies - use refs for values that change to avoid re-registering listener

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (doneDebounceRef.current) {
        clearTimeout(doneDebounceRef.current);
      }
    };
  }, []);

  // Sync AI edit queue to MessageInput whenever it changes
  useEffect(() => {
    console.log('🔄 Syncing AI edit queue to MessageInput:', aiEditQueue.length, 'items');
    window.dispatchEvent(new CustomEvent('aiQueueSync', {
      detail: { queue: aiEditQueue, conversationId }
    }));
  }, [aiEditQueue, conversationId]);

  // Send commands to iframe
  const sendCommand = useCallback((action: string, data?: any) => {
    if (iframeRef.current?.contentWindow) {
      const message = {
        source: 'gsap-preview',
        action,
        ...data,
      };
      // Only log non-routine commands (not seek which happens continuously)
      if (action !== 'seek') {
        console.log('📤 GsapSlidePreviewEnhanced - Sending command:', message);
      }
      iframeRef.current.contentWindow.postMessage(message, '*');
    }
  }, []);

  // Handle pending seek time after version restore
  // When iframe finishes loading and there's a pending seek time, seek to it instantly
  // This prevents showing slides 1, 2, 3 animating through before reaching the target time
  useEffect(() => {
    if (!playerState.isLoading && pendingSeekTimeRef.current !== null) {
      console.log('⏱️ Executing pending seek to:', pendingSeekTimeRef.current);
      // Use instantSeek command for immediate jump without animation through intermediate slides
      sendCommand('instantSeek', { time: pendingSeekTimeRef.current });
      clearPendingSeekTime();
      pendingSeekTimeRef.current = null;
    }
  }, [playerState.isLoading, clearPendingSeekTime, sendCommand]);

  // Handle filmstrip preview - seek to end to show completed slide state
  // When clicking a slide in the filmstrip, we want to show the finished slide (all animations complete)
  // rather than the initial blank state. When play is pressed, ShadowSlideRenderer.play() will restart from 0.
  useEffect(() => {
    if (!playerState.isLoading && previewSeekToEnd && playerState.duration > 0) {
      console.log('📍 [Preview] Seeking to end for filmstrip preview:', playerState.duration);
      // Use instantSeek command for immediate jump without animation
      sendCommand('instantSeek', { time: playerState.duration });
      setPreviewSeekToEnd(false);
    }
  }, [playerState.isLoading, previewSeekToEnd, playerState.duration, sendCommand, setPreviewSeekToEnd]);

  // Player control handlers
  const handlePlayPause = useCallback(() => {
    sendCommand('togglePlay');
  }, [sendCommand]);

  const handlePlay = useCallback(() => {
    sendCommand('play');
  }, [sendCommand]);

  const handlePause = useCallback(() => {
    sendCommand('pause');
  }, [sendCommand]);

  const handleSeek = useCallback((time: number) => {
    sendCommand('seek', { time });
  }, [sendCommand]);

  const handleRestart = useCallback(() => {
    sendCommand('restart');
  }, [sendCommand]);

  const handleStepBackward = useCallback(() => {
    sendCommand('step', { seconds: -10 });
  }, [sendCommand]);

  const handleStepForward = useCallback(() => {
    sendCommand('step', { seconds: 10 });
  }, [sendCommand]);

  const handleSpeedChange = useCallback((rate: number) => {
    sendCommand('setSpeed', { rate });
  }, [sendCommand]);

  const handleFullscreen = useCallback(() => {
    if (!isFullscreen) {
      containerRef.current?.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  }, [isFullscreen]);

  const handleEditMode = useCallback(() => {
    sendCommand('toggleEditMode');
    // Don't set isEditMode here - wait for confirmation from iframe
  }, [sendCommand]);

  // Edit toolbar handlers
  const handleSelectTool = useCallback(() => {
    sendCommand('editCommand', { command: 'selectTool' });
  }, [sendCommand]);

  const handleUndo = useCallback(() => {
    sendCommand('editCommand', { command: 'undo' });
  }, [sendCommand]);

  const handleRedo = useCallback(() => {
    sendCommand('editCommand', { command: 'redo' });
  }, [sendCommand]);

  const handleDelete = useCallback(() => {
    sendCommand('editCommand', { command: 'delete' });
  }, [sendCommand]);

  const handleDeselect = useCallback(() => {
    sendCommand('editCommand', { command: 'deselect' });
  }, [sendCommand]);

  const handleGroup = useCallback(() => {
    sendCommand('editCommand', { command: 'group' });
  }, [sendCommand]);

  // Grouping modal handlers
  const handleGroupMetadataSubmit = useCallback((metadata: GroupMetadata) => {
    console.log('📦 Sending group metadata to iframe:', metadata);
    sendCommand('groupMetadataResponse', { metadata });
    setShowGroupingModal(false);
  }, [sendCommand]);

  const handleGroupMetadataCancel = useCallback(() => {
    console.log('📦 Grouping cancelled by user');
    sendCommand('groupMetadataResponse', { metadata: null });
    setShowGroupingModal(false);
  }, [sendCommand]);

  const handleDone = useCallback(() => {
    // Guard against double-clicks
    if (isSaving) return;

    // Set saving state immediately (visual feedback)
    setIsSaving(true);

    // Send save command immediately - no debounce.
    // The previous 500ms debounce caused a race condition: parent state changes
    // could remount the component (changing React key) before the command fired,
    // destroying the iframe and losing all unsaved changes.
    sendCommand('editCommand', { command: 'done' });
    // Don't set isEditMode here - wait for editMode:false from iframe after exitEditMode
    // Saving state will be cleared when we get the confirmation
  }, [sendCommand, isSaving]);

  // Exit edit mode handlers
  const handleConfirmExit = useCallback(() => {
    setShowExitConfirmation(false);

    // Notify parent that user confirmed exit (with unsaved changes discarded)
    // This allows VideoWorkspacePanels to set pendingReturnToComposition flag
    window.dispatchEvent(new CustomEvent('editExitConfirmed', {
      detail: { conversationId, action: 'cancel' }
    }));

    // Send cancel command to iframe to discard changes
    sendCommand('editCommand', { command: 'cancel' });
    // Clear any pending AI edits
    setAIEditQueue([]);
    window.dispatchEvent(new CustomEvent('aiQueueClear', {
      detail: { conversationId }
    }));

    // Reload the iframe to revert visual changes to original state
    // This is needed because the DOM was modified during editing
    if (iframeRef.current) {
      iframeRef.current.src = iframeRef.current.src;
    }
  }, [sendCommand, conversationId]);

  const handleExitEditMode = useCallback(() => {
    // If there are unsaved changes (canUndo), show confirmation
    if (editToolbarState.canUndo) {
      setShowExitConfirmation(true);
    } else {
      // No changes, exit directly
      handleConfirmExit();
    }
  }, [editToolbarState.canUndo, handleConfirmExit]);

  // Handle Replace button click in edit mode - opens Pexels modal for selected asset
  const handleReplaceSelectedAsset = useCallback(async () => {
    if (!selectedAssetForReplace) {
      console.warn('🖼️ No asset selected for replacement');
      return;
    }

    console.log('🖼️ Replace button clicked for:', selectedAssetForReplace);

    // Determine the type for the modal (image, video, or icon)
    const modalType = selectedAssetForReplace.assetType === 'video'
      ? 'video'
      : selectedAssetForReplace.assetType === 'icon'
        ? 'icon'
        : 'image';

    // Set up for Pexels modal with PRECISE selector from edit mode
    setSelectedAsset({
      url: selectedAssetForReplace.currentUrl,
      type: modalType,
      elementId: selectedAssetForReplace.elementId,
      // This selector is GUARANTEED to match exactly one element
      elementPath: selectedAssetForReplace.selector,
      uniqueId: `edit-mode-${selectedAssetForReplace.elementId}`,
      layerIndex: 0,
      propertyType: selectedAssetForReplace.assetType === 'video' ? 'video' : 'img',
      isLocal: false
    });

    // Set replacing asset ID for Pexels modal
    setReplacingAsset(`edit-mode-${selectedAssetForReplace.elementId}`);

    // Fetch asset history for this element (use elementId as the identifier)
    if (slide.path && conversationId) {
      try {
        const historyResponse = await filesystemAPI.getAssetHistory(slide.path, conversationId);
        if (historyResponse.assetPositions) {
          const allHistoricalUrls = new Set<string>();
          Object.values(historyResponse.assetPositions).forEach((position: any) => {
            if (position.replacementHistory) {
              position.replacementHistory.forEach((entry: any) => {
                if (entry.url) allHistoricalUrls.add(entry.url);
              });
            }
          });
          setAssetHistory(Array.from(allHistoricalUrls).map(url => ({
            src: { original: url, large: url },
            reused: true,
            source: 'history'
          })));
        }
      } catch (error) {
        console.error('Failed to fetch asset history:', error);
        setAssetHistory([]);
      }
    }

    // Clear project assets (not fetching for now to simplify)
    setProjectAssets([]);

    // Open Pexels modal
    setShowPexelsModal(true);
  }, [selectedAssetForReplace, conversationId, slide.path]);

  // Handle quick replacement from QuickReplacePopover (one-click replacement)
  const handleQuickReplace = useCallback(async (url: string, source: string) => {
    if (!selectedAssetForReplace || !conversationId) {
      console.warn('[QuickReplace] No asset selected or no conversation ID');
      return;
    }

    console.log('[QuickReplace] Quick replacing asset:', {
      elementId: selectedAssetForReplace.elementId,
      selector: selectedAssetForReplace.selector,
      newUrl: url,
      source
    });

    try {
      // Show saving indicator
      setSaveStatus('saving');

      // Determine media type from the selected asset
      const mediaType = selectedAssetForReplace.assetType === 'video' ? 'video'
        : selectedAssetForReplace.assetType === 'lottie' ? 'lottie'
          : selectedAssetForReplace.assetType === 'icon' ? 'icon'
            : 'image';

      // Call the save API with asset replacement
      const result = await slidesAPI.saveSlideEdits({
        conversationId: conversationId,
        slideId: slideId,
        slidePath: slide.path,
        editData: {
          type: 'asset_replace',
          summary: { totalChanges: 1 },
          detailedJSON: {
            changes: [{
              selector: selectedAssetForReplace.selector,
              type: 'asset_replace',
              oldUrl: selectedAssetForReplace.currentUrl,
              newUrl: url,
              mediaType: mediaType,
              changes: {
                assetUrl: {
                  initial: selectedAssetForReplace.currentUrl,
                  final: url
                }
              }
            }]
          },
          assetMetadata: {
            layerIndex: 0,
            propertyType: mediaType === 'video' ? 'video' : 'img',
            elementPath: selectedAssetForReplace.selector,
            source: source,
            attribution: `Quick replace from ${source}`,
            mediaType: mediaType
          }
        },
        timestamp: new Date().toISOString()
      });

      console.log('[QuickReplace] Save successful:', result);

      // Refresh iframe to show updated content
      if (iframeRef.current) {
        const currentSrc = iframeRef.current.src;
        iframeRef.current.src = '';
        setTimeout(() => {
          if (iframeRef.current) {
            iframeRef.current.src = currentSrc;
          }
        }, 50);
      }

      // Show saved indicator
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 1500);

      // Notify parent of save completion
      onSaveComplete?.();

      // Show success toast
      window.dispatchEvent(new CustomEvent('showToast', {
        detail: { message: 'Asset replaced successfully', type: 'success' }
      }));

    } catch (error) {
      console.error('[QuickReplace] Error replacing asset:', error);
      setSaveStatus('idle');
      window.dispatchEvent(new CustomEvent('showToast', {
        detail: { message: 'Failed to replace asset', type: 'error' }
      }));
    }
  }, [selectedAssetForReplace, conversationId, slideId, slide.path, onSaveComplete]);

  // AI Edit Queue handlers
  const handleRemoveFromQueue = useCallback((id: string) => {
    console.log('🗑️ Removing from AI queue:', id);
    setAIEditQueue(prev => prev.filter(item => item.id !== id));
    // Also notify iframe
    sendCommand('removeAIEdit', { id });
  }, [sendCommand]);

  const handleSendAllAIEdits = useCallback(() => {
    if (aiEditQueue.length === 0) return;

    console.log('📤 Sending all AI edits to chat:', aiEditQueue);

    // Format AI edits for chat - full markdown for AI processing
    const fullMarkdown = formatAIEditsForChat(aiEditQueue);

    // Create summary text for user display
    const summaryText = `Requested ${aiEditQueue.length} AI edit${aiEditQueue.length !== 1 ? 's' : ''} for slide elements`;

    // Send to chat with both formats
    window.dispatchEvent(new CustomEvent('aiEditsReady', {
      detail: {
        fullMarkdown,  // Full markdown for AI
        summaryText,   // Summary for user display
        queue: aiEditQueue,
        conversationId
      }
    }));

    // Clear the queue immediately - we're done tracking these edits
    setAIEditQueue([]);
    setIsProcessingAI(false);

    // Notify iframe to clear its queue as well
    sendCommand('clearAIQueue', {});

    // Dispatch queue clear event to MessageInput
    window.dispatchEvent(new CustomEvent('aiQueueClear', {
      detail: { conversationId }
    }));

    console.log('✅ AI edits sent to chat and queue cleared');
  }, [aiEditQueue, sendCommand, conversationId]);

  // Format AI edits for chat
  const formatAIEditsForChat = (queue: AIEditQueueItem[]): string => {
    if (queue.length === 0) return '';

    let text = '## AI Edit Instructions\n\n';
    const slideReference = outlineSlideId || slide.path;
    text += `**Slide:** ${slideReference}\n`;
    text += `**Total Instructions:** ${queue.length}\n\n`;

    text += '### Element Modifications\n\n';

    queue.forEach((item, index) => {
      text += `#### ${index + 1}. ${item.elementName}\n`;
      text += `**Selector:** \`${item.technicalRef}\`\n`;
      text += `**Instruction:** ${item.instruction}\n\n`;
    });

    text += '---\n';
    text += '**Please apply these modifications to the slide elements as instructed.**\n';

    return text;
  };

  // Listen for remove requests from MessageInput
  useEffect(() => {
    const handleRemoveRequest = (event: CustomEvent) => {
      const { id } = event.detail;
      console.log('🗑️ Received remove request from MessageInput:', id);
      handleRemoveFromQueue(id);
    };

    window.addEventListener('aiEditRemoved', handleRemoveRequest as EventListener);
    return () => {
      window.removeEventListener('aiEditRemoved', handleRemoveRequest as EventListener);
    };
  }, [handleRemoveFromQueue]);

  // Listen for send requests from MessageInput
  useEffect(() => {
    const handleSendRequest = (event: CustomEvent) => {
      const { conversationId: eventConversationId } = event.detail;
      if (eventConversationId === conversationId) {
        console.log('📤 Received send request from MessageInput');
        handleSendAllAIEdits();
      }
    };

    window.addEventListener('requestSendAIEdits', handleSendRequest as EventListener);
    return () => {
      window.removeEventListener('requestSendAIEdits', handleSendRequest as EventListener);
    };
  }, [conversationId, handleSendAllAIEdits]);

  // Handle batched save from edit mode
  // Now uses proper HTML/CSS parsers via SourceFileService instead of fragile regex
  const handleBatchedSave = async (editData: any) => {
    try {
      console.log('💾 Processing batched save:', editData);

      // Show saving indicator
      setSaveStatus('saving');

      // Call API endpoint which handles source file parsing and saving
      const result = await slidesAPI.saveSlideEdits({
        conversationId: conversationId || '',
        slideId: slideId,
        slidePath: slide.path,
        editData: editData,
        timestamp: new Date().toISOString()
      });

      console.log('✅ Save successful:', result);

      // Refetch versions to update the version selector
      if (conversationId && slideId) {
        try {
          const versionData = await slidesAPI.listVersions(conversationId, slideId);
          setVersions(versionData.versions || []);
          setCurrentVersion(versionData.currentVersion);
          console.log('✅ [Versioning] Versions refreshed after save:', {
            enabled: versionData.enabled,
            count: versionData.versions?.length || 0,
            currentVersion: versionData.currentVersion
          });
        } catch (versionError) {
          console.error('❌ [Versioning] Failed to refresh versions:', versionError);
        }
      }

      // Refresh iframe to show updated content from GCS
      // Use clear-reload pattern (same as version switching) to force proper refresh
      if (iframeRef.current) {
        const currentSrc = iframeRef.current.src;
        iframeRef.current.src = '';
        setTimeout(() => {
          if (iframeRef.current) {
            iframeRef.current.src = currentSrc;
          }
        }, 50);
      }

      // Show saved indicator
      setSaveStatus('saved');

      // Notify parent that save completed (for video workspace to delay invalidation)
      console.log('📤 [GsapSlidePreviewEnhanced] Notifying parent of save completion');
      onSaveComplete?.();

      // Hide after brief display
      setTimeout(() => setSaveStatus('idle'), 1500);

    } catch (error) {
      console.error('❌ Error saving batched changes:', error);

      // Hide indicator on error
      setSaveStatus('idle');

      window.dispatchEvent(new CustomEvent('showToast', {
        detail: {
          message: 'Failed to save changes',
          type: 'error'
        }
      }));

      // On error: reload iframe to restore consistent state
      // Use clear-reload pattern for reliable refresh
      if (iframeRef.current) {
        const currentSrc = iframeRef.current.src;
        iframeRef.current.src = '';
        setTimeout(() => {
          if (iframeRef.current) {
            iframeRef.current.src = currentSrc;
          }
        }, 50);
      }
    }
  };

  // Version action handlers
  const handleSwitchVersion = useCallback(async (version: number) => {
    if (!conversationId || !slideId) return;

    setIsSwitchingVersion(true);

    try {
      console.log(`🔄 Switching to version ${version}`);

      await slidesAPI.switchToVersion(conversationId, slideId, version);

      // Reload iframe to show new version
      if (iframeRef.current) {
        const currentSrc = iframeRef.current.src;
        iframeRef.current.src = '';
        setTimeout(() => {
          if (iframeRef.current) {
            iframeRef.current.src = currentSrc;
          }
        }, 50);
      }

      // Reload version list
      const versionData = await slidesAPI.listVersions(conversationId, slideId);
      setVersions(versionData.versions || []);
      setCurrentVersion(versionData.currentVersion);

      window.dispatchEvent(new CustomEvent('showToast', {
        detail: {
          message: `Switched to version ${version}`,
          type: 'success'
        }
      }));
    } catch (error) {
      console.error('❌ Error switching version:', error);
      window.dispatchEvent(new CustomEvent('showToast', {
        detail: {
          message: 'Failed to switch version',
          type: 'error'
        }
      }));
    } finally {
      setIsSwitchingVersion(false);
    }
  }, [conversationId, slideId]);

  const handleDeleteVersion = useCallback(async (version: number) => {
    if (!conversationId || !slideId) return;

    try {
      await slidesAPI.deleteVersion(conversationId, slideId, version);

      // Reload version list
      const versionData = await slidesAPI.listVersions(conversationId, slideId);
      setVersions(versionData.versions || []);
      setCurrentVersion(versionData.currentVersion);

      window.dispatchEvent(new CustomEvent('showToast', {
        detail: {
          message: `Version ${version} deleted`,
          type: 'success'
        }
      }));
    } catch (error) {
      console.error('❌ Error deleting version:', error);
      window.dispatchEvent(new CustomEvent('showToast', {
        detail: {
          message: 'Failed to delete version',
          type: 'error'
        }
      }));
    }
  }, [conversationId, slideId]);

  const handleCompareVersions = useCallback(async (v1: number, v2: number) => {
    if (!conversationId || !slideId) return;

    try {
      const diff = await slidesAPI.compareVersions(conversationId, slideId, v1, v2);
      console.log('Version comparison:', diff);

      // Show comparison in a simple alert for now
      // TODO: Build a proper diff viewer UI
      const diffSummary = diff.changes.map(change =>
        `${change.file}: ${change.type}${change.diff ? ` (${change.diff})` : ''}`
      ).join('\n');

      alert(`Comparing v${v1} and v${v2}:\n\n${diffSummary || 'No differences found'}`);
    } catch (error) {
      console.error('❌ Error comparing versions:', error);
      window.dispatchEvent(new CustomEvent('showToast', {
        detail: {
          message: 'Failed to compare versions',
          type: 'error'
        }
      }));
    }
  }, [conversationId, slideId]);

  // Handle manual slide recompilation
  const handleRecompile = async () => {
    if (!conversationId) {
      console.warn('⚠️ Cannot recompile: no conversationId available');
      window.dispatchEvent(new CustomEvent('showToast', {
        detail: {
          message: 'Cannot recompile: conversation ID not available',
          type: 'error'
        }
      }));
      return;
    }

    // Check if slide has source files (only GSAP slides with /source directory)
    if (!slide.path.includes('slide.json')) {
      window.dispatchEvent(new CustomEvent('showToast', {
        detail: {
          message: 'This slide cannot be recompiled (legacy format)',
          type: 'warning'
        }
      }));
      return;
    }

    setIsRecompiling(true);

    try {
      console.log('🔄 Recompiling slide from source files...');

      const result = await slidesAPI.recompileSlide({
        conversationId: conversationId,
        slideId: slideId,
        slidePath: slide.path
      });

      console.log('✅ Recompile successful:', result);

      // Reload iframe to show updated slide
      if (iframeRef.current) {
        // Force reload by changing src
        const currentSrc = iframeRef.current.src;
        iframeRef.current.src = '';
        setTimeout(() => {
          if (iframeRef.current) {
            iframeRef.current.src = currentSrc;
          }
        }, 50);
      }

      window.dispatchEvent(new CustomEvent('showToast', {
        detail: {
          message: 'Slide recompiled and refreshed successfully',
          type: 'success'
        }
      }));

    } catch (error: any) {
      console.error('❌ Error recompiling slide:', error);

      // Check if it's a "no source directory" error
      const errorMessage = error?.response?.data?.error || 'Failed to recompile slide';
      const isLegacyFormat = error?.response?.data?.format === 'legacy';

      window.dispatchEvent(new CustomEvent('showToast', {
        detail: {
          message: isLegacyFormat
            ? 'This slide has no source files (legacy format)'
            : errorMessage,
          type: 'error'
        }
      }));
    } finally {
      setIsRecompiling(false);
    }
  };

  // Note: Helper functions for finding elements by selector are no longer needed
  // since we're now directly updating HTML and CSS strings in GSAP slides

  // Handle fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyboard = (e: KeyboardEvent) => {
      // Don't handle if typing in an input
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
        return;
      }

      switch (e.key) {
        case ' ':
        case 'k':
          e.preventDefault();
          handlePlayPause();
          break;
        case 'ArrowLeft':
          e.preventDefault();
          handleStepBackward();
          break;
        case 'ArrowRight':
          e.preventDefault();
          handleStepForward();
          break;
        case 'r':
        case 'R':
          e.preventDefault();
          handleRestart();
          break;
        case 'f':
        case 'F':
          if (!fullscreenKey) break;
          e.preventDefault();
          handleFullscreen();
          break;
        case '1':
          handleSpeedChange(1);
          break;
        case '2':
          handleSpeedChange(2);
          break;
        case 'Escape':
          if (isFullscreen) {
            document.exitFullscreen();
          }
          break;
      }
    };

    window.addEventListener('keydown', handleKeyboard);
    return () => window.removeEventListener('keydown', handleKeyboard);
  }, [
    handlePlayPause,
    handleStepBackward,
    handleStepForward,
    handleRestart,
    handleFullscreen,
    handleSpeedChange,
    isFullscreen,
    fullscreenKey,
  ]);

  // Pre-calculate scale for viewport
  const getInitialScale = () => {
    if (typeof window === 'undefined') return 0.5;
    const width = window.innerWidth * 0.9;
    const height = window.innerHeight * 0.9;
    return Math.min(
      width / resolution.width,
      height / resolution.height
    );
  };

  useEffect(() => {
    setScale(getInitialScale());
  }, []);

  // Initialize asset history - capture baseline assets before any replacements
  const initializeAssetHistory = useCallback(async (assetMap: Map<string, AssetInfo>) => {
    if (!conversationId) {
      console.warn('Cannot initialize asset history: conversationId is undefined');
      return;
    }

    try {
      // Convert assetMap to array format for API
      const assetsToInitialize = Array.from(assetMap.values()).map(asset => ({
        assetId: asset.uniqueId,
        url: asset.url,
        layerIndex: asset.layerIndex,
        propertyType: asset.propertyType,
        elementPath: asset.elementPath
      }));

      console.log('Initializing asset history for', assetsToInitialize.length, 'assets');

      // Call API to batch initialize
      const result = await filesystemAPI.initializeSlideAssets(conversationId, {
        slidePath: slide.path,
        assets: assetsToInitialize
      });

      console.log('Asset history initialized:', result);
    } catch (error) {
      console.error('Failed to initialize asset history:', error);
      // Don't throw - this is a non-critical enhancement
    }
  }, [conversationId, slide]);

  // Extract assets from slide with unique IDs
  // NOTE: This function was previously used by the Asset Panel (deprecated)
  // Kept for potential future use
  // @ts-ignore - Unused but preserved for potential future use
  const extractAssetsFromSlide = useCallback(async () => {
    console.log('Extracting assets from slide:', slide);

    // Handle both cases: layers in gsapData or directly on slide
    const layers = slide?.gsapData?.layers || (slide as any)?.layers;

    if (!slide || !layers) {
      console.log('No slide or layers found:', {
        slide,
        hasGsapData: !!slide?.gsapData,
        hasLayers: !!slide?.gsapData?.layers,
        hasDirectLayers: !!(slide as any)?.layers
      });
      return;
    }

    console.log('Processing', layers.length, 'layers');

    const assetMap = new Map<string, AssetInfo>();

    // Track counters for each type of asset per layer
    const layerCounters: Record<number, Record<string, number>> = {};

    const addAsset = (
      url: string,
      type: 'image' | 'video' | 'icon',
      layerIndex: number,
      propertyType: string,
      elementPath: string,
      elementId?: string,
      context?: string
    ) => {
      if (!url) return;

      // Initialize counter for this layer if needed
      if (!layerCounters[layerIndex]) {
        layerCounters[layerIndex] = {};
      }
      if (!layerCounters[layerIndex][propertyType]) {
        layerCounters[layerIndex][propertyType] = 0;
      }

      // Generate unique ID based on position
      const elementIndex = layerCounters[layerIndex][propertyType]++;
      const uniqueId = `layer-${layerIndex}-${propertyType}-${elementIndex}`;

      // Check if we've already added this exact asset (same URL at same position)
      const existingAsset = Array.from(assetMap.values()).find(a => a.uniqueId === uniqueId);
      if (existingAsset) {
        console.log('Asset already tracked at this position:', uniqueId);
        return;
      }

      const isLocal = url.startsWith('/') || url.startsWith('./');
      console.log('Adding asset:', { uniqueId, url, type, layerIndex, propertyType, elementPath, elementId });

      assetMap.set(uniqueId, {
        url,
        type,
        isLocal,
        uniqueId,
        layerIndex,
        elementPath,
        elementId,
        propertyType,
        context
      });
    };

    // Extract from each layer
    layers.forEach((layer: any, layerIndex: number) => {
      console.log(`Layer ${layerIndex}:`, { type: layer.type, hasHtml: !!layer.html, hasCss: !!layer.css, hasVideoSrc: !!layer.videoSrc });

      // Extract images from HTML content (handle both 'text' and 'gsap' types)
      if ((layer.type === 'gsap' || layer.type === 'text') && layer.html) {
        console.log(`Layer ${layerIndex} HTML preview:`, layer.html.substring(0, 200));

        // Extract img tags with class names and data-element-id for better identification
        const imgRegex = /<img([^>]+)>/gi;
        let imgMatch;
        let imgIndex = 0;
        while ((imgMatch = imgRegex.exec(layer.html)) !== null) {
          const imgTag = imgMatch[0];
          const srcMatch = /src=["']([^"']+)["']/.exec(imgTag);
          const classMatch = /class=["']([^"']+)["']/.exec(imgTag);
          const elementIdMatch = /data-element-id=["']([^"']+)["']/.exec(imgTag);

          if (srcMatch) {
            const src = srcMatch[1];
            const className = classMatch ? classMatch[1] : '';
            const elementId = elementIdMatch ? elementIdMatch[1] : undefined;
            // Convert space-separated class names to CSS selector format: "class1 class2" -> ".class1.class2"
            const elementPath = className
              ? '.' + className.split(/\s+/).filter(c => c).join('.')
              : `img[${imgIndex}]`;
            addAsset(src, 'image', layerIndex, 'img', elementPath, elementId, 'HTML img tag');
            imgIndex++;
          }
        }

        // Extract background images from inline styles in HTML
        const inlineStyleRegex = /style=["'][^"']*background-image:\s*url\(['"]?([^'")]+)['"]?\)[^"']*["']/gi;
        let styleIndex = 0;
        let styleMatch;
        while ((styleMatch = inlineStyleRegex.exec(layer.html)) !== null) {
          const src = styleMatch[1];
          addAsset(src, 'image', layerIndex, 'inline-bg', `element[${styleIndex}]`, undefined, 'Inline style background');
          styleIndex++;
        }

        // Extract video tags with data-element-id
        const videoRegex = /<video([^>]+)>/gi;
        let videoMatch;
        let videoIndex = 0;
        while ((videoMatch = videoRegex.exec(layer.html)) !== null) {
          const videoTag = videoMatch[0];
          const srcMatch = /src=["']([^"']+)["']/.exec(videoTag);
          const classMatch = /class=["']([^"']+)["']/.exec(videoTag);
          const elementIdMatch = /data-element-id=["']([^"']+)["']/.exec(videoTag);

          if (srcMatch) {
            const src = srcMatch[1];
            const className = classMatch ? classMatch[1] : '';
            const elementId = elementIdMatch ? elementIdMatch[1] : undefined;
            // Convert space-separated class names to CSS selector format: "class1 class2" -> ".class1.class2"
            const elementPath = className
              ? '.' + className.split(/\s+/).filter(c => c).join('.')
              : `video[${videoIndex}]`;
            addAsset(src, 'video', layerIndex, 'video', elementPath, elementId, 'HTML video tag');
            videoIndex++;
          }
        }

        // Extract source tags within video elements
        const sourceRegex = /<source([^>]+)>/gi;
        let sourceMatch;
        let sourceIndex = 0;
        while ((sourceMatch = sourceRegex.exec(layer.html)) !== null) {
          const sourceTag = sourceMatch[0];
          const srcMatch = /src=["']([^"']+)["']/.exec(sourceTag);

          if (srcMatch) {
            const src = srcMatch[1];
            const isVideo = /\.(mp4|webm|ogg|mov)$/i.test(src);
            addAsset(src, isVideo ? 'video' : 'image', layerIndex, 'source', `source[${sourceIndex}]`, undefined, 'HTML source tag');
            sourceIndex++;
          }
        }
      }

      // Extract video sources from dedicated video layers
      if (layer.type === 'video' && layer.videoSrc) {
        addAsset(layer.videoSrc, 'video', layerIndex, 'video-layer', 'videoSrc', undefined, 'Video layer');
      }

      // Extract background images from CSS
      if (layer.css) {
        const bgRegex = /background-image:\s*url\(['"]?([^'")]+)['"]?\)/gi;
        let bgMatch;
        let bgIndex = 0;
        while ((bgMatch = bgRegex.exec(layer.css)) !== null) {
          const src = bgMatch[1];
          addAsset(src, 'image', layerIndex, 'css-bg', `bg[${bgIndex}]`, undefined, 'CSS background');
          bgIndex++;
        }

        // Also check for background shorthand
        const bgShorthandRegex = /background:\s*[^;]*url\(['"]?([^'")]+)['"]?\)/gi;
        let shorthandMatch;
        let shorthandIndex = 0;
        while ((shorthandMatch = bgShorthandRegex.exec(layer.css)) !== null) {
          const src = shorthandMatch[1];
          // Only add if not already added by the more specific regex
          if (!Array.from(assetMap.values()).some(a => a.url === src && a.layerIndex === layerIndex)) {
            addAsset(src, 'image', layerIndex, 'css-bg-shorthand', `bg-shorthand[${shorthandIndex}]`, undefined, 'CSS background');
            shorthandIndex++;
          }
        }
      }
    });

    // Also check metadata for additional assets
    if (conversationId) {
      try {
        const metadataPath = slide.path.replace('/slide.json', '/info.json');
        // Use filesystemAPI instead of hardcoded fetch
        const response = await filesystemAPI.read(metadataPath, conversationId);
        if (response && response.content) {
          const metadata = JSON.parse(response.content);
          if (metadata && metadata.assets) {
            Object.entries(metadata.assets).forEach(([key, asset]: [string, any]) => {
              if (asset.url) {
                // Metadata assets are stored separately, use -1 for layer index
                addAsset(asset.url, asset.type || 'image', -1, 'metadata', key, undefined, `Metadata: ${key}`);
              }
            });
          }
        }
      } catch (error) {
        // Silently ignore if metadata file doesn't exist
        console.debug('No metadata file found (this is normal):', error);
      }
    }

    console.log('Total assets found:', assetMap.size, Array.from(assetMap.values()));
    setAssets(assetMap);

    // Initialize asset history if we found assets
    if (assetMap.size > 0) {
      await initializeAssetHistory(assetMap);
    }
  }, [slide, conversationId, initializeAssetHistory]);

  // Handle asset replacement
  // NOTE: This function was previously used by the Asset Panel (deprecated)
  // Kept for potential future use
  // @ts-ignore - Unused but preserved for potential future use
  const handleAssetReplace = useCallback(async (asset: AssetInfo) => {
    setSelectedAsset(asset);

    // Fetch full slide history to show ALL unique assets ever used
    if (conversationId && slide) {
      try {
        // Fetch complete slide history (not just for this position)
        const fullHistoryData = await filesystemAPI.getAssetHistory(slide.path, conversationId);

        // Extract all unique assets from history
        const allHistoricalAssets: any[] = [];
        const seenUrls = new Set<string>();

        console.log('📚 Full history data:', fullHistoryData);
        console.log('🎯 Current asset being replaced:', asset.url);

        // From new format: assetPositions
        if (fullHistoryData.assetPositions) {
          console.log('📦 Processing assetPositions, count:', Object.keys(fullHistoryData.assetPositions).length);
          Object.entries(fullHistoryData.assetPositions).forEach(([positionId, position]: [string, any]) => {
            console.log(`  Position ${positionId}:`, position);
            if (position.replacementHistory && Array.isArray(position.replacementHistory)) {
              console.log(`    Replacement history length: ${position.replacementHistory.length}`);
              position.replacementHistory.forEach((item: any, index: number) => {
                console.log(`      [${index}] URL: ${item.url?.substring(0, 60)}...`);
                if (item.url && !seenUrls.has(item.url)) {
                  seenUrls.add(item.url);
                  // Include ALL historical assets, not just those different from current
                  // The UI will handle marking the current one
                  allHistoricalAssets.push({
                    url: item.url,
                    source: item.source || 'unknown',
                    attribution: item.attribution,
                    replacedAt: item.replacedAt,
                    isCurrent: item.url === asset.url
                  });
                  console.log(`        ✅ Added to history (isCurrent: ${item.url === asset.url})`);
                } else if (seenUrls.has(item.url)) {
                  console.log(`        ⏭️  Skipped - already seen`);
                }
              });
            }
          });
        }

        console.log('📊 Total historical assets extracted from assetPositions:', allHistoricalAssets.length);
        console.log('📋 Assets:', allHistoricalAssets.map(a => ({ url: a.url.substring(0, 50), source: a.source })));

        // From old format: history array (for backward compatibility)
        // Only process if no assetPositions exist (to avoid duplicates)
        if (!fullHistoryData.assetPositions && fullHistoryData.history && Array.isArray(fullHistoryData.history)) {
          console.log('📜 Processing legacy history array (no assetPositions found), count:', fullHistoryData.history.length);
          fullHistoryData.history.forEach((item: any, index: number) => {
            // Add both original and new URLs
            if (item.originalUrl && !seenUrls.has(item.originalUrl)) {
              seenUrls.add(item.originalUrl);
              allHistoricalAssets.push({
                url: item.originalUrl,
                source: 'original',
                attribution: null,
                replacedAt: null,
                isCurrent: item.originalUrl === asset.url
              });
              console.log(`  [${index}] ✅ Added original URL`);
            }
            if (item.newUrl && !seenUrls.has(item.newUrl)) {
              seenUrls.add(item.newUrl);
              allHistoricalAssets.push({
                url: item.newUrl,
                source: item.source || 'pexels',
                attribution: item.attribution,
                replacedAt: item.replacedAt,
                isCurrent: item.newUrl === asset.url
              });
              console.log(`  [${index}] ✅ Added new URL`);
            }
          });
        } else if (fullHistoryData.assetPositions) {
          console.log('✅ Skipping legacy history array (assetPositions already processed)');
        }

        console.log('✅ Final asset history count:', allHistoricalAssets.length);
        setAssetHistory(allHistoricalAssets);

        // Also fetch project-wide asset library
        const projectLib = await filesystemAPI.getProjectAssetLibrary(conversationId);
        setProjectAssets(Object.values(projectLib.library || {}));
      } catch (error) {
        console.error('Failed to fetch asset history:', error);
        setAssetHistory([]);
      }
    }

    setShowPexelsModal(true);
  }, [conversationId, slide]);

  // Handle Pexels selection
  const handlePexelsSelect = useCallback(async (pexelsAsset: any) => {
    if (!selectedAsset || !slide || !conversationId) return;

    setReplacingAsset(selectedAsset.url);

    try {
      // Extract new URL and attribution info
      // Check if this is a reused asset or a new Pexels asset
      const isReused = pexelsAsset.reused === true;
      // Check media type: video, lottie, icon, or image
      const isVideo = !!(pexelsAsset.video_files && pexelsAsset.video_files.length > 0);
      const isLottie = !!(pexelsAsset.isLottie || pexelsAsset.source === 'lottiefiles' ||
        (pexelsAsset.src?.original && /\.json(\?.*)?$/i.test(pexelsAsset.src.original)) ||
        (pexelsAsset.jsonUrl));
      const isIcon = !!(pexelsAsset.isIcon || pexelsAsset.source === 'iconify' ||
        (pexelsAsset.src?.original && pexelsAsset.src.original.includes('api.iconify.design')));

      // Extract URL based on asset type
      let newUrl: string | undefined;
      if (isReused) {
        newUrl = pexelsAsset.src?.original;
      } else if (isLottie) {
        // For Lottie: get JSON URL
        newUrl = pexelsAsset.jsonUrl || pexelsAsset.src?.original || pexelsAsset.lottieUrl;
      } else if (isIcon) {
        // For Icons: get SVG URL (may include color parameters)
        newUrl = pexelsAsset.src?.original || pexelsAsset.iconUrl;
      } else if (isVideo) {
        // For videos: get best quality video file URL (prefer HD)
        const hdVideo = pexelsAsset.video_files.find((f: any) => f.quality === 'hd');
        const sdVideo = pexelsAsset.video_files.find((f: any) => f.quality === 'sd');
        newUrl = hdVideo?.link || sdVideo?.link || pexelsAsset.video_files[0]?.link;
      } else {
        // For images: existing logic
        newUrl = pexelsAsset.src?.large || pexelsAsset.src?.original;
      }

      // Validate that we have a valid URL
      if (!newUrl) {
        console.error('❌ Failed to extract URL from asset:', pexelsAsset);
        window.dispatchEvent(new CustomEvent('showToast', {
          detail: { message: 'Could not extract URL from selected asset', type: 'error' }
        }));
        setReplacingAsset(null);
        return;
      }

      const attribution = isReused
        ? (pexelsAsset.photographer || pexelsAsset.source || 'Reused')
        : isLottie
          ? (pexelsAsset.creator || pexelsAsset.author || 'LottieFiles')
          : isIcon
            ? (pexelsAsset.author || 'Iconify')
            : (pexelsAsset.photographer || pexelsAsset.user?.name || 'Pexels');
      const source = isReused
        ? (pexelsAsset.source || 'reused')
        : isLottie ? 'lottiefiles' : isIcon ? 'iconify' : 'pexels';
      const assetId = selectedAsset.uniqueId; // Use the unique position-based ID
      // Determine media type for element conversion (4 types: image, video, icon, lottie)
      const mediaType: 'image' | 'video' | 'icon' | 'lottie' = isLottie ? 'lottie' : isIcon ? 'icon' : isVideo ? 'video' : 'image';

      // Build selector for the asset element
      // CRITICAL: Use data-element-id for precise targeting, fall back to elementPath
      // NEVER use wildcard '*' as it would replace ALL images
      const selector = selectedAsset.elementId
        ? `[data-element-id="${selectedAsset.elementId}"]`
        : selectedAsset.elementPath;

      // Validate that we have a valid selector
      if (!selector) {
        console.error('❌ Cannot replace asset: no selector available', selectedAsset);
        window.dispatchEvent(new CustomEvent('showToast', {
          detail: { message: 'Cannot identify which asset to replace. Please try again.', type: 'error' }
        }));
        setReplacingAsset(null);
        return;
      }

      console.log('🖼️ Replacing asset via saveSlideEdits:', {
        oldUrl: selectedAsset.url,
        newUrl,
        selector,
        elementId: selectedAsset.elementId,
        slideId: slideId,
        mediaType,
        isVideo,
        isLottie,
        isIcon
      });

      // Show saving indicator
      setSaveStatus('saving');

      // Use saveSlideEdits which creates proper GCS version
      const result = await slidesAPI.saveSlideEdits({
        conversationId: conversationId,
        slideId: slideId,
        slidePath: slide.path,
        editData: {
          type: 'asset_replace',
          summary: { totalChanges: 1 },
          detailedJSON: {
            changes: [{
              selector: selector,
              type: 'asset_replace',
              oldUrl: selectedAsset.url,
              newUrl: newUrl,
              mediaType: mediaType, // 'image' or 'video' - for element type conversion
              changes: {
                assetUrl: {
                  initial: selectedAsset.url,
                  final: newUrl
                }
              }
            }]
          },
          // Include metadata for tracking
          assetMetadata: {
            layerIndex: selectedAsset.layerIndex,
            propertyType: selectedAsset.propertyType,
            elementPath: selectedAsset.elementPath,
            source: source,
            attribution: isReused ? attribution :
              isLottie ? `Animation by ${attribution} from LottieFiles` :
                isIcon ? `Icon by ${attribution} from Iconify` :
                  `${isVideo ? 'Video' : 'Photo'} by ${attribution} from Pexels`,
            mediaType: mediaType // Track media type in metadata (image, video, icon, lottie)
          }
        },
        timestamp: new Date().toISOString()
      });

      console.log('✅ Asset replaced with GCS versioning:', result);

      // Update version state from response
      if (result.versioning?.currentVersion) {
        const version = typeof result.versioning.currentVersion === 'string'
          ? parseInt(result.versioning.currentVersion, 10)
          : result.versioning.currentVersion;
        setCurrentVersion(version);
        // Refresh version list
        try {
          const versionData = await slidesAPI.listVersions(conversationId, slideId);
          setVersions(versionData.versions || []);
          setVersioningEnabled(versionData.enabled);
          console.log('✅ Version list refreshed after asset replacement');
        } catch (versionError) {
          console.warn('Failed to refresh version list:', versionError);
        }
      }

      // Also track in asset history (for asset library features)
      try {
        await filesystemAPI.updateAssetHistory(conversationId, {
          slidePath: slide.path,
          assetId,
          originalUrl: selectedAsset.url,
          newUrl,
          source,
          attribution: isReused ? attribution :
            isLottie ? `Animation by ${attribution} from LottieFiles` :
              isIcon ? `Icon by ${attribution} from Iconify` :
                `${isVideo ? 'Video' : 'Photo'} by ${attribution} from Pexels`,
          // Add position metadata
          layerIndex: selectedAsset.layerIndex,
          propertyType: selectedAsset.propertyType,
          elementPath: selectedAsset.elementPath
        });
      } catch (historyError) {
        console.warn('Failed to update asset history:', historyError);
      }

      // Update local state
      setAssets(prev => {
        const updated = new Map(prev);
        updated.delete(selectedAsset.url);
        updated.set(newUrl, {
          ...selectedAsset,
          url: newUrl,
          isLocal: false,
        });
        return updated;
      });

      // Refresh iframe to show updated content from GCS
      // Use clear-reload pattern for reliable refresh
      if (iframeRef.current) {
        const currentSrc = iframeRef.current.src;
        iframeRef.current.src = '';
        setTimeout(() => {
          if (iframeRef.current) {
            iframeRef.current.src = currentSrc;
          }
        }, 50);
      }

      // Show saved indicator
      setSaveStatus('saved');

      // Hide after brief display
      setTimeout(() => setSaveStatus('idle'), 1500);

      console.log('Asset replaced successfully with GCS versioning');
    } catch (error) {
      console.error('Failed to replace asset:', error);

      // Hide indicator on error
      setSaveStatus('idle');

      window.dispatchEvent(new CustomEvent('showToast', {
        detail: {
          message: 'Failed to replace asset. Please try again.',
          type: 'error'
        }
      }));
    } finally {
      setReplacingAsset(null);
      setShowPexelsModal(false);
      setSelectedAsset(null);
    }
  }, [selectedAsset, slide, conversationId]);

  // Scale calculation effect - recalculates when drawer opens/closes or container resizes
  useEffect(() => {
    const updateScale = () => {
      if (containerRef.current) {
        const containerRect = containerRef.current.getBoundingClientRect();
        // Account for drawer width when open (420px)
        const drawerOffset = isDrawerOpen && !isFullscreen && !isEditMode ? 420 : 0;
        const containerWidth = containerRect.width - drawerOffset;
        // Only subtract controls height if controls are rendered
        // When renderControls=false (used in VideoWorkspacePanels), use full container height
        const controlsHeight = renderControls ? 140 : 0;
        const containerHeight = containerRect.height - controlsHeight;

        const scaleX = containerWidth / resolution.width;
        const scaleY = containerHeight / resolution.height;
        const optimalScale = Math.min(scaleX, scaleY);

        setScale(Math.max(0.1, optimalScale)); // Ensure minimum scale
      }
    };

    // Initial calculation with slight delay to ensure container is properly sized
    const timeoutId = setTimeout(updateScale, 50);

    updateScale();
    window.addEventListener('resize', updateScale);

    if (containerRef.current) {
      const resizeObserver = new ResizeObserver(updateScale);
      resizeObserver.observe(containerRef.current);

      return () => {
        clearTimeout(timeoutId);
        window.removeEventListener('resize', updateScale);
        resizeObserver.disconnect();
      };
    }

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', updateScale);
    };
  }, [slide, isDrawerOpen, isFullscreen, isEditMode, renderControls, resolution]);

  // Expose methods via ref
  useImperativeHandle(ref, () => ({
    play: handlePlay,
    pause: handlePause,
    togglePlay: handlePlayPause,
    seek: handleSeek,
    restart: handleRestart,
    step: (seconds) => (seconds > 0 ? handleStepForward() : handleStepBackward()),
    setSpeed: handleSpeedChange,
    toggleEditMode: handleEditMode,
    undo: handleUndo,
    redo: handleRedo,
    deleteSelection: handleDelete,
    deselectAll: handleDeselect,
    groupSelection: handleGroup,
    finishEdit: handleDone,
    cancelEdit: handleExitEditMode,
    replaceAsset: handleReplaceSelectedAsset,
    selectTool: handleSelectTool,
  }));

  return (
    <div
      ref={containerRef}
      className={cn(
        "flex flex-col h-full",
        isFullscreen && "fixed inset-0 z-50 bg-background",
        className
      )}
    >
      {/* Top Bar - Minimal and Elegant */}
      {!isFullscreen && renderControls && (
        <div className="flex-shrink-0 bg-card/80 backdrop-blur-xl border-b border-border z-30">
          {/* Always show navigation bar */}
          <div className="px-4 py-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {onBackToGallery && (
                  <Button
                    onClick={onBackToGallery}
                    variant="ghost"
                    size="sm"
                    className="transition-all"
                  >
                    <ChevronLeft className="h-4 w-4 mr-1" />
                    Gallery
                  </Button>
                )}
                {isTemplate && onApplyTemplate && (
                  <Button
                    onClick={async () => {
                      setIsApplyingTemplate(true);
                      try {
                        await onApplyTemplate();
                      } finally {
                        setIsApplyingTemplate(false);
                      }
                    }}
                    disabled={isApplyingTemplate}
                    variant="default"
                    size="sm"
                    className="transition-all"
                  >
                    {isApplyingTemplate ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                        Applying...
                      </>
                    ) : (
                      <>
                        <svg className="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        Apply Template
                      </>
                    )}
                  </Button>
                )}
                <div className="text-sm font-medium text-foreground">
                  {playerState.slideTitle || slide.title}
                </div>
              </div>

              <div className="flex items-center gap-2">
                {/* Version Switcher - Show when conversationId available */}
                {!isEditMode && conversationId && (
                  <>
                    {versioningEnabled && versions.length > 0 ? (
                      <VersionSwitcher
                        versions={versions}
                        currentVersion={currentVersion}
                        onSwitch={handleSwitchVersion}
                        onOpenHistory={() => {
                          setShowVersionHistory(true);
                          setIsDrawerOpen(true);
                        }}
                        isSwitching={isSwitchingVersion}
                      />
                    ) : (
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-8 gap-2 px-3 opacity-60 cursor-not-allowed border-border/60"
                        title="No versions yet - Make an edit to create the first version"
                        disabled
                      >
                        <History className="h-3.5 w-3.5" />
                        <span className="text-xs">No Versions</span>
                      </Button>
                    )}
                  </>
                )}

                {/* Refresh/Recompile Button - Always visible when not in edit mode */}
                {!isEditMode && (
                  <Button
                    onClick={handleRecompile}
                    variant="ghost"
                    size="sm"
                    className="transition-all"
                    title={conversationId ? "Recompile slide from source files" : "Recompile unavailable (no conversation context)"}
                    disabled={isRecompiling || !conversationId}
                  >
                    {isRecompiling ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <RefreshCcw className="h-4 w-4" />
                    )}
                  </Button>
                )}

                {/* Publish/Share Button */}
                {!isEditMode && conversationId && !slide.metadata?.isTemplate && (
                  <Button
                    onClick={() => setShowPublishModal(true)}
                    variant={publishStatus?.isPublished ? 'secondary' : 'ghost'}
                    size="sm"
                    className="transition-all"
                    title={publishStatus?.isPublished ? 'Manage share link' : 'Publish slide'}
                    disabled={isLoadingPublishStatus}
                  >
                    {isLoadingPublishStatus ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : publishStatus?.isPublished ? (
                      <Globe className="h-4 w-4 text-green-500" />
                    ) : (
                      <Share2 className="h-4 w-4" />
                    )}
                  </Button>
                )}

                {/* Info Toggle */}
                {!isEditMode && (
                  <Button
                    onClick={() => setIsDrawerOpen(!isDrawerOpen)}
                    variant={isDrawerOpen ? 'secondary' : 'ghost'}
                    size="sm"
                    className="transition-all"
                    title="Toggle slide details"
                  >
                    <Info className="h-4 w-4" />
                  </Button>
                )}

                {/* Edit Mode */}
                {!isEditMode && (
                  <Button
                    onClick={handleEditMode}
                    variant="ghost"
                    size="sm"
                    className="transition-all"
                    title="Toggle edit mode"
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                )}

                {/* Fullscreen */}
                <Button
                  onClick={handleFullscreen}
                  variant="ghost"
                  size="sm"
                  className="transition-all"
                  title="Toggle fullscreen"
                >
                  {isFullscreen ? (
                    <Minimize2 className="h-4 w-4" />
                  ) : (
                    <Maximize2 className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>
          </div>

          {/* Show edit toolbar below navigation when in edit mode */}
          {isEditMode && (
            <div className="border-t border-border px-4 py-2">
              <div className="flex items-center justify-between gap-4">
                <GsapEditToolbar
                  selectedCount={editToolbarState.selectedCount}
                  canUndo={editToolbarState.canUndo}
                  canRedo={editToolbarState.canRedo}
                  isSaving={isSaving}
                  isAssetSelected={!!selectedAssetForReplace}
                  isReplacing={!!replacingAsset}
                  onSelectTool={handleSelectTool}
                  onUndo={handleUndo}
                  onRedo={handleRedo}
                  onDelete={handleDelete}
                  onGroup={handleGroup}
                  onDeselect={handleDeselect}
                  onDone={handleDone}
                  onExit={handleExitEditMode}
                  onReplace={handleReplaceSelectedAsset}
                  onQuickReplace={handleQuickReplace}
                  suggestionMetadata={selectedAssetForReplace?.suggestionMetadata}
                />
                {/* NOTE: Save status indicator disabled (no auto-save) */}
                {/* <SaveStatusIndicator status={saveStatus} /> */}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Main Content Area */}
      <div className="flex-1 relative overflow-hidden">
        {/* Viewport - Modern elevated card preview */}
        <div className={cn(
          "absolute inset-0 flex items-center justify-center transition-all duration-300",
          isDrawerOpen && !isFullscreen && !isEditMode && "right-[420px]"
        )}>
          <div
            className="preview-frame"
            style={{
              width: `${resolution.width * scale}px`,
              height: `${resolution.height * scale}px`,
            }}
          >
            <div
              className="relative overflow-hidden studio-slide-shadow"
              style={{
                width: `${resolution.width * scale}px`,
                height: `${resolution.height * scale}px`,
              }}
            >
              <iframe
                ref={iframeRef}
                src={viewerUrl}
                className="absolute inset-0 w-full h-full border-0"
                title={slide.title}
                sandbox="allow-same-origin allow-scripts allow-forms allow-modals"
                style={{
                  width: `${resolution.width}px`,
                  height: `${resolution.height}px`,
                  transform: `scale(${scale})`,
                  transformOrigin: 'top left',
                }}
              />

              {/* Loading Overlay - Don't show in edit mode */}
              {playerState.isLoading && !isEditMode && (
                <div className="absolute inset-0 bg-background/95 backdrop-blur-sm flex items-center justify-center pointer-events-none">
                  <div className="text-center">
                    <div className="w-12 h-12 border-4 border-border border-t-primary rounded-full animate-spin mx-auto mb-4" />
                    <p className="text-muted-foreground text-sm">Loading slide...</p>
                  </div>
                </div>
              )}

              {/* Save Status Indicator - Elegant floating pill */}
              {saveStatus !== 'idle' && (
                <div className="absolute top-4 left-1/2 -translate-x-1/2 z-50 pointer-events-none animate-in fade-in slide-in-from-top-2 duration-200">
                  <div className={`
                    flex items-center gap-2 px-4 py-2 rounded-full shadow-lg backdrop-blur-md
                    ${saveStatus === 'saving'
                      ? 'bg-zinc-900/80 text-white'
                      : 'bg-emerald-500/90 text-white'}
                    transition-all duration-300
                  `}>
                    {saveStatus === 'saving' ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        <span className="text-sm font-medium">Saving...</span>
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                        <span className="text-sm font-medium">Saved</span>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Overlay Player Controls - Floating at Bottom - Hide in edit mode */}
        {!isEditMode && renderControls && (
          <AutoHidePlayerControls
            isPlaying={playerState.isPlaying}
            isFullscreen={isFullscreen}
            className="absolute inset-x-0 bottom-0"
          >
            <GsapPlayerControls
              {...playerState}
              onPlayPause={handlePlayPause}
              onPlay={handlePlay}
              onPause={handlePause}
              onSeek={handleSeek}
              onRestart={handleRestart}
              onStepBackward={handleStepBackward}
              onStepForward={handleStepForward}
              onSpeedChange={handleSpeedChange}
              onFullscreen={handleFullscreen}
            />
          </AutoHidePlayerControls>
        )}

        {/* Side Drawer - Versions & Audio */}
        {isDrawerOpen && !isFullscreen && !isEditMode && (
          <div className="absolute top-0 right-0 h-full w-[420px] bg-background/98 backdrop-blur-2xl border-l-2 border-border/40 shadow-2xl shadow-black/10 flex flex-col overflow-hidden animate-in slide-in-from-right duration-300">
            {/* Subtle gradient overlay for depth */}
            <div className="absolute inset-0 bg-gradient-to-l from-transparent via-transparent to-primary/[0.02] pointer-events-none" />
            {/* Determine what content to show */}
            {(() => {
              const hasVersions = versioningEnabled && versions.length > 0;
              const hasAudio = !!conversationId;

              // If both versions and audio available, show tabs
              if (hasVersions && hasAudio) {
                return (
                  <Tabs defaultValue={showVersionHistory ? "versions" : "audio"} className="flex-1 min-h-0 flex flex-col overflow-hidden relative z-10">
                    <TabsList className="w-full rounded-none bg-muted/30 h-auto p-1.5 gap-1.5 border-b border-border/30">
                      <TabsTrigger
                        value="versions"
                        className="flex-1 rounded-lg py-2.5 text-sm font-medium transition-all data-[state=active]:bg-background data-[state=active]:shadow-sm data-[state=active]:text-foreground data-[state=inactive]:text-muted-foreground hover:text-foreground"
                        onClick={() => setShowVersionHistory(true)}
                      >
                        Versions ({versions.length})
                      </TabsTrigger>
                      <TabsTrigger
                        value="audio"
                        className="flex-1 rounded-lg py-2.5 text-sm font-medium transition-all data-[state=active]:bg-background data-[state=active]:shadow-sm data-[state=active]:text-foreground data-[state=inactive]:text-muted-foreground hover:text-foreground"
                        onClick={() => setShowVersionHistory(false)}
                      >
                        Audio
                      </TabsTrigger>
                    </TabsList>

                    <TabsContent value="versions" className="flex-1 min-h-0 overflow-hidden mt-0 h-full">
                      <VersionHistoryPanel
                        conversationId={conversationId || ''}
                        slideId={slideId}
                        versions={versions}
                        currentVersion={currentVersion}
                        onRestore={handleSwitchVersion}
                        onDelete={handleDeleteVersion}
                        onCompare={handleCompareVersions}
                        isRestoring={isSwitchingVersion}
                        className="h-full max-h-full"
                      />
                    </TabsContent>

                    <TabsContent value="audio" className="flex-1 min-h-0 overflow-hidden mt-0 max-w-full h-full">
                      <ScriptAudioPanel
                        slideId={slide.id}
                        slideIdentifier={slideId}
                        conversationId={conversationId}
                        initialScript={slide.metadata?.script_text || ''}
                        className="h-full max-h-full border-0"
                      />
                    </TabsContent>
                  </Tabs>
                );
              }

              // If only versions, show versions panel directly
              if (hasVersions) {
                return (
                  <VersionHistoryPanel
                    conversationId={conversationId || ''}
                    slideId={slideId}
                    versions={versions}
                    currentVersion={currentVersion}
                    onRestore={handleSwitchVersion}
                    onDelete={handleDeleteVersion}
                    onCompare={handleCompareVersions}
                    isRestoring={isSwitchingVersion}
                    className="h-full max-h-full"
                  />
                );
              }

              // If only audio, show audio panel directly
              if (hasAudio) {
                return (
                  <ScriptAudioPanel
                    slideId={slide.id}
                    slideIdentifier={slideId}
                    conversationId={conversationId}
                    initialScript={slide.metadata?.script_text || ''}
                    className="h-full max-h-full border-0"
                  />
                );
              }

              // Fallback: empty state
              return (
                <div className="flex-1 flex items-center justify-center text-muted-foreground text-sm">
                  No content available
                </div>
              );
            })()}
          </div>
        )}
      </div>

      {/* Pexels Search Modal */}
      {showPexelsModal && selectedAsset && (
        <Dialog open={showPexelsModal} onOpenChange={setShowPexelsModal}>
          <DialogContent className="max-w-[95vw] w-full h-[90vh] max-h-[90vh] p-0 !flex !flex-col overflow-hidden">
            <DialogHeader className="px-6 pt-6 pb-4 flex-shrink-0">
              <DialogTitle>Replace Asset {selectedAsset.type === 'icon' ? 'with Icon' : 'with Stock Media'}</DialogTitle>
              <DialogDescription>
                {selectedAsset.type === 'icon'
                  ? 'Search and select an icon from 200,000+ options'
                  : `Search for free stock ${selectedAsset.type === 'video' ? 'videos' : 'images'} to replace the current asset`}
              </DialogDescription>
            </DialogHeader>

            <div className="flex-1 min-h-0 flex flex-col">
              <PexelsSearchModal
                isOpen={true}
                onClose={() => setShowPexelsModal(false)}
                onSelect={handlePexelsSelect}
                type={selectedAsset.type}
                defaultQuery={
                  selectedAssetForReplace?.suggestionMetadata?.searchTerms?.[0] ||
                  (selectedAsset.context?.includes('background') ? 'abstract background' : 'business')
                }
                currentAsset={selectedAsset ? { url: selectedAsset.url, uniqueId: selectedAsset.uniqueId } : undefined}
                assetHistory={assetHistory || []}
                slideAssets={Array.from(assets.values()).map(a => ({ url: a.url, uniqueId: a.uniqueId }))}
                recentProjectAssets={projectAssets.slice(0, 10)}
                conversationId={conversationId}
                suggestedSearchTerms={selectedAssetForReplace?.suggestionMetadata?.searchTerms}
                semanticContext={selectedAssetForReplace?.suggestionMetadata?.semanticContext}
              />
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Grouping Metadata Modal */}
      <GroupingModal
        isOpen={showGroupingModal}
        elementCount={groupingElementCount}
        onSubmit={handleGroupMetadataSubmit}
        onCancel={handleGroupMetadataCancel}
      />

      {/* Publish Slide Modal */}
      {conversationId && slideId && (
        <PublishSlideModal
          isOpen={showPublishModal}
          onClose={() => setShowPublishModal(false)}
          conversationId={conversationId}
          slideId={slideId}
          slideTitle={slide.title}
          publishStatus={publishStatus}
          onPublishStatusChange={setPublishStatus}
        />
      )}

      {/* Exit Edit Mode Confirmation Dialog */}
      <ConfirmationDialog
        open={showExitConfirmation}
        onOpenChange={setShowExitConfirmation}
        title="Exit Edit Mode?"
        description="All unsaved changes will be lost. This action cannot be undone."
        confirmText="Exit Without Saving"
        cancelText="Keep Editing"
        variant="destructive"
        onConfirm={handleConfirmExit}
      />
    </div>
  );
});

GsapSlidePreviewEnhanced.displayName = 'GsapSlidePreviewEnhanced';